import { NotificationChannel } from '@/schema'

import type {
  NotificationChannelSettings,
  NotificationPreferences,
  NotificationPreferencesAPI,
  PreferenceConfig,
  PreferenceEventKey,
  PreferenceKey,
} from './types'

/**
 * Map UI preference keys to API event keys (for comment events in PreferenceConfig)
 */
const COMMENT_UI_TO_API_KEY_MAP: Record<
  Extract<
    PreferenceKey,
    'comments.new_comment' | 'comments.comment_resolved' | 'comments.mentions'
  >,
  PreferenceEventKey
> = {
  'comments.new_comment': 'comment_new',
  'comments.comment_resolved': 'comment_resolved',
  'comments.mentions': 'comment_mention',
}

/**
 * Mapping from NotificationChannel enum to channel settings
 */
const ENUM_TO_SETTINGS_MAP: Record<
  NotificationChannel,
  NotificationChannelSettings
> = {
  [NotificationChannel.BOTH]: { email: true, in_app: true },
  [NotificationChannel.EMAIL]: { email: true, in_app: false },
  [NotificationChannel.IN_APP]: { email: false, in_app: true },
  [NotificationChannel.NONE]: { email: false, in_app: false },
}

/**
 * Convert API channel settings (booleans) to UI NotificationChannel enum
 */
export function channelSettingsToEnum(
  settings: NotificationChannelSettings
): NotificationChannel {
  if (settings.email && settings.in_app) return NotificationChannel.BOTH
  if (settings.email) return NotificationChannel.EMAIL
  if (settings.in_app) return NotificationChannel.IN_APP
  return NotificationChannel.NONE
}

/**
 * Convert UI NotificationChannel enum to API channel settings (booleans)
 */
export function enumToChannelSettings(
  channel: NotificationChannel
): NotificationChannelSettings {
  return (
    ENUM_TO_SETTINGS_MAP[channel] ||
    ENUM_TO_SETTINGS_MAP[NotificationChannel.NONE]
  )
}

/**
 * Transform API response to UI preferences format
 * Handles all preferences from preference_config object
 */
export function apiToUIPreferences(
  apiPreferences: NotificationPreferencesAPI
): NotificationPreferences {
  const result: Partial<NotificationPreferences> = {}

  const preferenceConfig = apiPreferences.preference_config
  if (preferenceConfig) {
    // Handle comment events from PreferenceConfig
    Object.entries(COMMENT_UI_TO_API_KEY_MAP).forEach(([uiKey, apiKey]) => {
      const settings = preferenceConfig[apiKey]
      result[uiKey as PreferenceKey] = settings
        ? channelSettingsToEnum(settings)
        : NotificationChannel.NONE
    })

    // Handle deadlines
    if (preferenceConfig.workflow_deadline) {
      result['deadlines.deliverable_deadline_reminders'] =
        channelSettingsToEnum(preferenceConfig.workflow_deadline)
    }

    // Handle assignments
    if (preferenceConfig.deliverable_assign) {
      result['assignments.deliverable_assignment_changes'] =
        channelSettingsToEnum(preferenceConfig.deliverable_assign)
    }
  }

  return result as NotificationPreferences
}

/**
 * Transform UI preferences to API request format
 * Returns UpdatePreferencesRequest with preference_config
 */
export function uiToAPIPreferences(uiPreferences: NotificationPreferences): {
  preference_config: PreferenceConfig
} {
  // Build PreferenceConfig for comments
  const preferenceConfig: Partial<PreferenceConfig> = {}

  // Add comment preferences
  Object.entries(COMMENT_UI_TO_API_KEY_MAP).forEach(([uiKey, apiKey]) => {
    const channel = uiPreferences[uiKey as PreferenceKey]
    if (channel) {
      preferenceConfig[apiKey] = enumToChannelSettings(channel)
    }
  })

  // Add deadlines
  const deadlinesChannel =
    uiPreferences['deadlines.deliverable_deadline_reminders']
  if (deadlinesChannel) {
    preferenceConfig.workflow_deadline = enumToChannelSettings(deadlinesChannel)
  }

  // Add assignments
  const assignmentsChannel =
    uiPreferences['assignments.deliverable_assignment_changes']
  if (assignmentsChannel) {
    preferenceConfig.deliverable_assign =
      enumToChannelSettings(assignmentsChannel)
  }

  return {
    preference_config: preferenceConfig as PreferenceConfig,
  }
}
